const _jsxFileName = '';
function _optionalChain(ops) {
  let lastAccessLHS = undefined;
  let value = ops[0];
  let i = 1;
  while (i < ops.length) {
    const op = ops[i];
    const fn = ops[i + 1];
    i += 2;
    if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) {
      return undefined;
    }
    if (op === 'access' || op === 'optionalAccess') {
      lastAccessLHS = value;
      value = fn(value);
    } else if (op === 'call' || op === 'optionalCall') {
      value = fn((...args) => value.call(lastAccessLHS, ...args));
      lastAccessLHS = undefined;
    }
  }
  return value;
}
import React, { useState, useEffect, Fragment } from 'react';
import {
  Search,
  Filter,
  ArrowLeft,
  User,
  ShoppingBag,
  Calendar,
  ChevronDown,
  ChevronUp,
  Download,
  Mail,
  Phone,
  FileText,
  RefreshCw,
  DollarSign,
} from 'lucide-react';

// Tier configuration with point thresholds
const TIER_CONFIG = {
  Sapphire: {
    minPoints: 0,
    maxPoints: 4999,
    label: 'Sapphire',
    color: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    icon: '◆',
  },
  Ruby: {
    minPoints: 5000,
    maxPoints: 14999,
    label: 'Ruby',
    color: 'bg-red-100 text-red-700 border-red-200',
    icon: '♥',
  },
  Emerald: {
    minPoints: 15000,
    maxPoints: 49999,
    label: 'Emerald',
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    icon: '✨',
  },
  Diamond: {
    minPoints: 50000,
    maxPoints: Infinity,
    label: 'Diamond',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: '◇',
  },
};

// Calculate tier based on points
const getTierFromPoints = (points) => {
  if (points >= 50000) return 'Diamond';
  if (points >= 15000) return 'Emerald';
  if (points >= 5000) return 'Ruby';
  return 'Sapphire';
};

// Calculate points from total spent (100 PHP = 1 point)
const calculatePointsFromSpent = (totalSpent) => {
  return Math.floor(totalSpent / 100);
};

// API base URL
const API_BASE_URL = import.meta.env.VITE_API_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
});

const AdminMembers = ({ members, onNavigate, user }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedMember, setExpandedMember] = useState(null);
  const [memberOrders, setMemberOrders] = useState({});
  const [loadingOrders, setLoadingOrders] = useState({});

  // State for filtering and sorting
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTier, setFilterTier] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [localMembers, setLocalMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiTotalRevenue, setApiTotalRevenue] = useState(null);

  // Fetch members from database (ONLY members, exclude admins)
  const fetchMembersFromDatabase = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/members`, { headers: authHeaders() });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        // Filter out admin users - check multiple possible role field names
        const filteredMembers = result.data.filter((m) => {
          const role = m.role || m.user_role || '';
          const isAdmin = role === 'admin' || role === 'Admin' || role === 'ADMIN';

          if (isAdmin) {
            return false;
          }
          return true;
        });

        // Calculate proper tier based on points/total_spent
        const membersWithTier = filteredMembers.map((m) => {
          // Calculate points from total spent (100 PHP = 1 point)
          let points = m.points || 0;
          const totalSpent = m.total_spent || m.totalSpent || 0;

          // If points is 0 but totalSpent > 0, calculate points from totalSpent
          if (points === 0 && totalSpent > 0) {
            points = calculatePointsFromSpent(totalSpent);
          }

          // Determine tier based on points
          const tier = getTierFromPoints(points);

          return {
            id: m.id,
            name: m.name || m.first_name + ' ' + m.last_name || m.username || 'Member',
            email: m.email,
            phone: m.phone || '',
            avatar: m.avatar || '',
            role: m.role || 'member',
            membership_tier: tier,
            points: points,
            status: m.status || 'Active',
            totalSpent: totalSpent,
            ordersCount: m.orders_count || m.ordersCount || 0,
            joinDate:
              m.joinDate ||
              _optionalChain([
                m,
                'access',
                (_) => _.created_at,
                'optionalAccess',
                (_2) => _2.split,
                'call',
                (_3) => _3('T'),
                'access',
                (_4) => _4[0],
              ]) ||
              new Date().toLocaleDateString(),
            created_at: m.created_at,
            last_login: m.last_login,
          };
        });

        setLocalMembers(membersWithTier);

        if (result.totalRevenue !== undefined) {
          setApiTotalRevenue(result.totalRevenue);
        }
      } else {
        // Filter props members
        const filtered = members.filter((m) => {
          const role = m.role || '';
          return role !== 'admin' && role !== 'Admin' && role !== 'ADMIN';
        });
        setLocalMembers(filtered);
      }
    } catch (error) {
      setError('Failed to load members. Using sample data.');
      const filtered = members.filter((m) => {
        const role = m.role || '';
        return role !== 'admin' && role !== 'Admin' && role !== 'ADMIN';
      });
      setLocalMembers(filtered);
    } finally {
      setLoading(false);
    }
  };

  // Fetch orders for a specific member
  const fetchMemberOrders = async (userId) => {
    setLoadingOrders((prev) => ({ ...prev, [userId]: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/orders/get?user_id=${userId}`, {
        headers: authHeaders(),
      });
      const data = await response.json();

      if (data.success && data.orders) {
        setMemberOrders((prev) => ({ ...prev, [userId]: data.orders }));
      } else {
        setMemberOrders((prev) => ({ ...prev, [userId]: [] }));
      }
    } catch (error) {
      setMemberOrders((prev) => ({ ...prev, [userId]: [] }));
    } finally {
      setLoadingOrders((prev) => ({ ...prev, [userId]: false }));
    }
  };

  // Fetch on component mount
  useEffect(() => {
    fetchMembersFromDatabase();
  }, []);

  // Fetch orders when expanding a member
  useEffect(() => {
    if (expandedMember !== null) {
      const member = localMembers.find((m) => m.id === expandedMember);
      if (member && member.id && !memberOrders[member.id]) {
        fetchMemberOrders(member.id);
      }
    }
  }, [expandedMember, localMembers, memberOrders]);

  // Filter and sort members (exclude admins)
  const filteredMembers = localMembers
    .filter((m) => {
      // Exclude any admin users
      const role = m.role || '';
      if (role === 'admin' || role === 'Admin' || role === 'ADMIN') return false;

      const matchesSearch =
        (
          _optionalChain([
            m,
            'access',
            (_5) => _5.name,
            'optionalAccess',
            (_6) => _6.toLowerCase,
            'call',
            (_7) => _7(),
          ]) || ''
        ).includes(searchTerm.toLowerCase()) ||
        (
          _optionalChain([
            m,
            'access',
            (_8) => _8.email,
            'optionalAccess',
            (_9) => _9.toLowerCase,
            'call',
            (_10) => _10(),
          ]) || ''
        ).includes(searchTerm.toLowerCase()) ||
        (
          _optionalChain([
            m,
            'access',
            (_11) => _11.phone,
            'optionalAccess',
            (_12) => _12.toLowerCase,
            'call',
            (_13) => _13(),
          ]) || ''
        ).includes(searchTerm.toLowerCase());

      const matchesStatus = filterStatus === 'all' || m.status === filterStatus;
      const matchesTier = filterTier === 'all' || m.membership_tier === filterTier;

      return matchesSearch && matchesStatus && matchesTier;
    })
    .sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'date':
          const dateA = a.joinDate
            ? new Date(a.joinDate).getTime()
            : a.created_at
              ? new Date(a.created_at).getTime()
              : 0;
          const dateB = b.joinDate
            ? new Date(b.joinDate).getTime()
            : b.created_at
              ? new Date(b.created_at).getTime()
              : 0;
          comparison = dateA - dateB;
          break;
        case 'spent':
          comparison = (a.totalSpent || 0) - (b.totalSpent || 0);
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  // Calculate stats (only members, not admins)
  const totalMembers = localMembers.length;
  const activeMembers = localMembers.filter(
    (m) => m.status === 'Active' || m.status === 'VIP'
  ).length;
  const vipMembers = localMembers.filter(
    (m) => m.membership_tier === 'Diamond' || m.membership_tier === 'Emerald'
  ).length;
  const totalRevenue =
    apiTotalRevenue !== null
      ? apiTotalRevenue
      : localMembers.reduce((sum, m) => sum + (m.totalSpent || 0), 0);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Get tier badge with icon
  const getTierBadge = (tier = 'Sapphire') => {
    const config = TIER_CONFIG[tier] || TIER_CONFIG.Sapphire;
    return config.color;
  };

  const getTierIcon = (tier = 'Sapphire') => {
    const config = TIER_CONFIG[tier] || TIER_CONFIG.Sapphire;
    return config.icon;
  };

  // Get status badge
  const getStatusBadge = (status = 'Active') => {
    switch (status) {
      case 'VIP':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'Active':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'Inactive':
        return 'bg-stone-100 text-stone-500 border-stone-200';
      default:
        return 'bg-stone-100 text-stone-500 border-stone-200';
    }
  };

  // Download as CSV
  const downloadCSV = () => {
    const headers = [
      'Name',
      'Email',
      'Phone',
      'Membership Tier',
      'Points',
      'Status',
      'Join Date',
      'Total Spent',
      'Orders Count',
    ];

    const csvData = filteredMembers.map((m) => [
      m.name || '',
      m.email || '',
      m.phone || '',
      m.membership_tier || 'Sapphire',
      m.points || 0,
      m.status || 'Active',
      m.joinDate || m.created_at || '',
      m.totalSpent || 0,
      m.ordersCount || 0,
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `sparkling-gems-members-${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleExpand = (id) => {
    setExpandedMember(expandedMember === id ? null : id);
  };

  // Helper to get member orders
  const getMemberOrders = (memberId) => {
    return memberOrders[memberId] || [];
  };

  // Format order status for display
  const getOrderStatusBadge = (status) => {
    const statusLower =
      _optionalChain([
        status,
        'optionalAccess',
        (_14) => _14.toLowerCase,
        'call',
        (_15) => _15(),
      ]) || '';
    switch (statusLower) {
      case 'delivered':
      case 'picked up':
        return 'bg-green-100 text-green-700';
      case 'shipped':
        return 'bg-blue-100 text-blue-700';
      case 'preparing':
      case 'processing':
        return 'bg-purple-100 text-purple-700';
      case 'paid':
        return 'bg-emerald-100 text-emerald-700';
      case 'pending':
        return 'bg-orange-100 text-orange-700';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-stone-100 text-stone-600';
    }
  };

  return React.createElement(
    'div',
    {
      className: 'pt-44 pb-12 min-h-screen bg-stone-50',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 339 },
    },
    React.createElement(
      'div',
      {
        className: 'max-w-7xl mx-auto px-6',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 340 },
      },

      /* Header */
      React.createElement(
        'div',
        {
          className: 'flex flex-col md:flex-row justify-between items-center mb-8 gap-4',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 343 },
        },
        React.createElement(
          'div',
          { __self: this, __source: { fileName: _jsxFileName, lineNumber: 344 } },
          (user?.role === 'admin' || (user?.permissions || []).includes('view_dashboard')) &&
            React.createElement(
              'button',
              {
                onClick: () => onNavigate('admin-dashboard'),
                className:
                  'flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-2 transition-colors',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 345 },
              },
              React.createElement(ArrowLeft, {
                size: 14,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 346 },
              }),
              ' Back to Dashboard'
            ),
          React.createElement(
            'h1',
            {
              className: 'font-serif text-3xl text-stone-900',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 348 },
            },
            'Member Directory'
          ),
          React.createElement(
            'p',
            {
              className: 'text-stone-500 font-light mt-2 text-sm',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 349 },
            },
            'Manage all registered members and their information.'
          )
        ),

        /* Action buttons */
        React.createElement(
          'div',
          {
            className: 'flex items-center gap-3',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 353 },
          },
          React.createElement(
            'button',
            {
              onClick: fetchMembersFromDatabase,
              className:
                'px-4 py-2 border border-stone-200 text-stone-600 text-xs uppercase tracking-widest hover:bg-stone-50 transition-colors rounded-sm flex items-center gap-2',
              disabled: loading,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 354 },
            },

            React.createElement(RefreshCw, {
              size: 14,
              className: loading ? 'animate-spin' : '',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 359 },
            }),
            loading ? 'Loading...' : 'Refresh'
          ),
          React.createElement(
            'div',
            {
              className: 'relative group',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 362 },
            },
            React.createElement(
              'button',
              {
                className:
                  'px-4 py-2 bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors rounded-sm flex items-center gap-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 363 },
              },
              React.createElement(Download, {
                size: 14,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 364 },
              }),
              ' Export'
            ),
            React.createElement(
              'div',
              {
                className:
                  'absolute right-0 top-full mt-2 w-40 bg-white border border-stone-200 shadow-lg rounded-sm py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 366 },
              },
              React.createElement(
                'button',
                {
                  onClick: downloadCSV,
                  className:
                    'w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 flex items-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 367 },
                },

                React.createElement(FileText, {
                  size: 12,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 371 },
                }),
                ' CSV File'
              )
            )
          )
        )
      ),

      /* Error message */
      error &&
        React.createElement(
          'div',
          {
            className:
              'bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 mb-6 rounded-sm',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 380 },
          },
          React.createElement(
            'p',
            {
              className: 'text-sm',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 381 },
            },
            error
          )
        ),

      /* Stats Cards */
      React.createElement(
        'div',
        {
          className: 'grid grid-cols-1 md:grid-cols-4 gap-6 mb-8',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 386 },
        },
        React.createElement(
          'div',
          {
            className: 'bg-white border border-stone-200 shadow-sm rounded-sm p-6',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 387 },
          },
          React.createElement(
            'p',
            {
              className: 'text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 388 },
            },
            'Total Members'
          ),
          React.createElement(
            'p',
            {
              className: 'font-serif text-3xl text-stone-900',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 389 },
            },
            totalMembers
          ),
          React.createElement(
            'p',
            {
              className: 'text-[10px] text-stone-400 mt-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 390 },
            },
            'Registered members'
          )
        ),

        React.createElement(
          'div',
          {
            className: 'bg-white border border-stone-200 shadow-sm rounded-sm p-6',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 393 },
          },
          React.createElement(
            'p',
            {
              className: 'text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 394 },
            },
            'Active Members'
          ),
          React.createElement(
            'p',
            {
              className: 'font-serif text-3xl text-stone-900',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 395 },
            },
            activeMembers
          ),
          React.createElement(
            'p',
            {
              className: 'text-[10px] text-green-600 mt-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 396 },
            },
            totalMembers > 0 ? ((activeMembers / totalMembers) * 100).toFixed(1) : 0,
            '% of total'
          )
        ),

        React.createElement(
          'div',
          {
            className: 'bg-white border border-stone-200 shadow-sm rounded-sm p-6',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 399 },
          },
          React.createElement(
            'p',
            {
              className: 'text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 400 },
            },
            'VIP Members'
          ),
          React.createElement(
            'p',
            {
              className: 'font-serif text-3xl text-stone-900',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 401 },
            },
            vipMembers
          ),
          React.createElement(
            'p',
            {
              className: 'text-[10px] text-gold-600 mt-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 402 },
            },
            'Diamond & Emerald tier'
          )
        ),

        React.createElement(
          'div',
          {
            className: 'bg-white border border-stone-200 shadow-sm rounded-sm p-6',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 405 },
          },
          React.createElement(
            'p',
            {
              className: 'text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 406 },
            },
            'Total Revenue'
          ),
          React.createElement(
            'p',
            {
              className: 'font-serif text-3xl text-stone-900',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 407 },
            },
            formatCurrency(totalRevenue)
          ),
          React.createElement(
            'p',
            {
              className: 'text-[10px] text-stone-400 mt-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 408 },
            },
            'From member purchases'
          )
        )
      ),

      React.createElement(
        'div',
        {
          className: 'bg-white border border-stone-200 shadow-sm rounded-sm overflow-hidden',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 412 },
        },
        /* Toolbar */
        React.createElement(
          'div',
          {
            className:
              'p-6 border-b border-stone-100 flex flex-col md:flex-row gap-4 justify-between bg-stone-50/30',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 414 },
          },
          React.createElement(
            'div',
            {
              className: 'relative flex-1 max-w-md',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 415 },
            },
            React.createElement(Search, {
              className: 'absolute left-3 top-1/2 -translate-y-1/2 text-stone-400',
              size: 16,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 416 },
            }),
            React.createElement('input', {
              type: 'text',
              placeholder: 'Search by name or email...',
              value: searchTerm,
              onChange: (e) => setSearchTerm(e.target.value),
              className:
                'w-full pl-10 pr-4 py-2 border border-stone-200 rounded-sm focus:border-gold-500 focus:outline-none text-sm',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 417 },
            })
          ),

          /* Filters and sort */
          React.createElement(
            'div',
            {
              className: 'flex items-center gap-3 flex-wrap',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 427 },
            },
            React.createElement(
              'div',
              {
                className: 'flex items-center gap-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 428 },
              },
              React.createElement(Filter, {
                size: 14,
                className: 'text-stone-400',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 429 },
              }),
              React.createElement(
                'select',
                {
                  value: filterStatus,
                  onChange: (e) => setFilterStatus(e.target.value),
                  className:
                    'border border-stone-200 rounded-sm px-3 py-2 text-sm focus:border-gold-500 focus:outline-none bg-white',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 430 },
                },

                React.createElement(
                  'option',
                  {
                    value: 'all',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 435 },
                  },
                  'All Status'
                ),
                React.createElement(
                  'option',
                  {
                    value: 'Active',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 436 },
                  },
                  'Active'
                ),
                React.createElement(
                  'option',
                  {
                    value: 'VIP',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 437 },
                  },
                  'VIP'
                ),
                React.createElement(
                  'option',
                  {
                    value: 'Inactive',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 438 },
                  },
                  'Inactive'
                )
              )
            ),

            React.createElement(
              'select',
              {
                value: filterTier,
                onChange: (e) => setFilterTier(e.target.value),
                className:
                  'border border-stone-200 rounded-sm px-3 py-2 text-sm focus:border-gold-500 focus:outline-none bg-white',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 442 },
              },

              React.createElement(
                'option',
                {
                  value: 'all',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 447 },
                },
                'All Tiers'
              ),
              React.createElement(
                'option',
                {
                  value: 'Sapphire',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 448 },
                },
                'Sapphire'
              ),
              React.createElement(
                'option',
                {
                  value: 'Ruby',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 449 },
                },
                'Ruby'
              ),
              React.createElement(
                'option',
                {
                  value: 'Emerald',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 450 },
                },
                'Emerald'
              ),
              React.createElement(
                'option',
                {
                  value: 'Diamond',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 451 },
                },
                'Diamond'
              )
            ),

            React.createElement(
              'select',
              {
                value: `${sortBy}-${sortOrder}`,
                onChange: (e) => {
                  const [newSortBy, newSortOrder] = e.target.value.split('-');
                  setSortBy(newSortBy);
                  setSortOrder(newSortOrder);
                },
                className:
                  'border border-stone-200 rounded-sm px-3 py-2 text-sm focus:border-gold-500 focus:outline-none bg-white',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 454 },
              },

              React.createElement(
                'option',
                {
                  value: 'date-desc',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 463 },
                },
                'Newest First'
              ),
              React.createElement(
                'option',
                {
                  value: 'date-asc',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 464 },
                },
                'Oldest First'
              ),
              React.createElement(
                'option',
                {
                  value: 'name-asc',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 465 },
                },
                'Name A-Z'
              ),
              React.createElement(
                'option',
                {
                  value: 'name-desc',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 466 },
                },
                'Name Z-A'
              ),
              React.createElement(
                'option',
                {
                  value: 'spent-desc',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 467 },
                },
                'Highest Spent'
              ),
              React.createElement(
                'option',
                {
                  value: 'spent-asc',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 468 },
                },
                'Lowest Spent'
              )
            )
          )
        ),

        /* Table */
        loading
          ? React.createElement(
              'div',
              {
                className: 'p-12 text-center',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 475 },
              },
              React.createElement('div', {
                className:
                  'w-8 h-8 border-2 border-gold-600 border-t-transparent rounded-full animate-spin mx-auto mb-4',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 476 },
              }),
              React.createElement(
                'p',
                {
                  className: 'text-stone-500 text-sm',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 477 },
                },
                'Loading members...'
              )
            )
          : React.createElement(
              'div',
              {
                className: 'overflow-x-auto',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 480 },
              },
              React.createElement(
                'table',
                {
                  className: 'w-full text-left border-collapse',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 481 },
                },
                React.createElement(
                  'thead',
                  {
                    className:
                      'bg-stone-100 text-stone-500 text-[10px] uppercase tracking-widest font-bold',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 482 },
                  },
                  React.createElement(
                    'tr',
                    { __self: this, __source: { fileName: _jsxFileName, lineNumber: 483 } },
                    React.createElement(
                      'th',
                      {
                        className: 'px-6 py-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 484 },
                      },
                      'Member Info'
                    ),
                    React.createElement(
                      'th',
                      {
                        className: 'px-6 py-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 485 },
                      },
                      'Status'
                    ),
                    React.createElement(
                      'th',
                      {
                        className: 'px-6 py-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 486 },
                      },
                      'Tier'
                    ),
                    React.createElement(
                      'th',
                      {
                        className: 'px-6 py-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 487 },
                      },
                      'Points'
                    ),
                    React.createElement(
                      'th',
                      {
                        className: 'px-6 py-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 488 },
                      },
                      'Joined'
                    ),
                    React.createElement(
                      'th',
                      {
                        className: 'px-6 py-4 text-right',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 489 },
                      },
                      'Total Spent'
                    ),
                    React.createElement(
                      'th',
                      {
                        className: 'px-6 py-4 text-center',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 490 },
                      },
                      'Orders'
                    ),
                    React.createElement('th', {
                      className: 'px-6 py-4',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 491 },
                    })
                  )
                ),
                React.createElement(
                  'tbody',
                  {
                    className: 'divide-y divide-stone-100',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 494 },
                  },
                  filteredMembers.length > 0
                    ? filteredMembers.map((member) => {
                        const orders = getMemberOrders(member.id);
                        const tierIcon = getTierIcon(member.membership_tier);

                        return React.createElement(
                          Fragment,
                          {
                            key: member.id,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 501 },
                          },
                          React.createElement(
                            'tr',
                            {
                              className: `hover:bg-stone-50/50 transition-colors cursor-pointer ${expandedMember === member.id ? 'bg-stone-50' : ''}`,
                              onClick: () => toggleExpand(member.id),
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 502 },
                            },
                            React.createElement(
                              'td',
                              {
                                className: 'px-6 py-4',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 503 },
                              },
                              React.createElement(
                                'div',
                                {
                                  className: 'flex items-center gap-3',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 504 },
                                },
                                React.createElement(
                                  'div',
                                  {
                                    className:
                                      'w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center text-stone-500 overflow-hidden',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 505 },
                                  },
                                  member.avatar
                                    ? React.createElement('img', {
                                        src: member.avatar,
                                        alt: member.name,
                                        className: 'w-full h-full object-cover',
                                        __self: this,
                                        __source: { fileName: _jsxFileName, lineNumber: 507 },
                                      })
                                    : React.createElement(User, {
                                        size: 18,
                                        __self: this,
                                        __source: { fileName: _jsxFileName, lineNumber: 509 },
                                      })
                                ),
                                React.createElement(
                                  'div',
                                  {
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 512 },
                                  },
                                  React.createElement(
                                    'span',
                                    {
                                      className: 'font-serif text-stone-900 block',
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 513 },
                                    },
                                    member.name || 'No Name'
                                  ),
                                  React.createElement(
                                    'div',
                                    {
                                      className:
                                        'flex items-center gap-2 text-xs text-stone-500 mt-1',
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 514 },
                                    },
                                    React.createElement(Mail, {
                                      size: 10,
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 515 },
                                    }),
                                    ' ',
                                    member.email
                                  ),
                                  member.phone &&
                                    React.createElement(
                                      'div',
                                      {
                                        className:
                                          'flex items-center gap-2 text-xs text-stone-500 mt-0.5',
                                        __self: this,
                                        __source: { fileName: _jsxFileName, lineNumber: 518 },
                                      },
                                      React.createElement(Phone, {
                                        size: 10,
                                        __self: this,
                                        __source: { fileName: _jsxFileName, lineNumber: 519 },
                                      }),
                                      ' ',
                                      member.phone
                                    )
                                )
                              )
                            ),
                            React.createElement(
                              'td',
                              {
                                className: 'px-6 py-4',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 525 },
                              },
                              React.createElement(
                                'span',
                                {
                                  className: `text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${getStatusBadge(member.status)}`,
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 526 },
                                },
                                member.status || 'Active'
                              )
                            ),
                            React.createElement(
                              'td',
                              {
                                className: 'px-6 py-4',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 530 },
                              },
                              React.createElement(
                                'span',
                                {
                                  className: `text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${getTierBadge(member.membership_tier)} flex items-center gap-1 w-fit`,
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 531 },
                                },
                                React.createElement(
                                  'span',
                                  {
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 532 },
                                  },
                                  tierIcon
                                ),
                                ' ',
                                member.membership_tier || 'Sapphire'
                              )
                            ),
                            React.createElement(
                              'td',
                              {
                                className: 'px-6 py-4 text-sm font-bold text-gold-600',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 535 },
                              },
                              _optionalChain([
                                member,
                                'access',
                                (_16) => _16.points,
                                'optionalAccess',
                                (_17) => _17.toLocaleString,
                                'call',
                                (_18) => _18(),
                              ]) || 0
                            ),
                            React.createElement(
                              'td',
                              {
                                className: 'px-6 py-4 text-xs text-stone-600',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 538 },
                              },
                              React.createElement(
                                'div',
                                {
                                  className: 'flex items-center gap-2',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 539 },
                                },
                                React.createElement(Calendar, {
                                  size: 14,
                                  className: 'text-stone-400',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 540 },
                                }),
                                member.joinDate ||
                                  (member.created_at
                                    ? new Date(member.created_at).toLocaleDateString()
                                    : 'N/A')
                              )
                            ),
                            React.createElement(
                              'td',
                              {
                                className: 'px-6 py-4 text-right font-mono text-sm text-stone-900',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 544 },
                              },
                              React.createElement(
                                'div',
                                {
                                  className: 'flex items-center justify-end gap-1',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 545 },
                                },
                                React.createElement(DollarSign, {
                                  size: 12,
                                  className: 'text-gold-600',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 546 },
                                }),
                                _optionalChain([
                                  member,
                                  'access',
                                  (_19) => _19.totalSpent,
                                  'optionalAccess',
                                  (_20) => _20.toLocaleString,
                                  'call',
                                  (_21) => _21(),
                                ]) || '0'
                              )
                            ),
                            React.createElement(
                              'td',
                              {
                                className: 'px-6 py-4 text-center text-sm font-bold',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 550 },
                              },
                              member.ordersCount || 0
                            ),
                            React.createElement(
                              'td',
                              {
                                className: 'px-6 py-4 text-right text-stone-400',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 553 },
                              },
                              expandedMember === member.id
                                ? React.createElement(ChevronUp, {
                                    size: 16,
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 554 },
                                  })
                                : React.createElement(ChevronDown, {
                                    size: 16,
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 554 },
                                  })
                            )
                          ),

                          /* Expanded Details Row with Real Orders */
                          expandedMember === member.id &&
                            React.createElement(
                              'tr',
                              {
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 560 },
                              },
                              React.createElement(
                                'td',
                                {
                                  colSpan: 8,
                                  className:
                                    'bg-stone-50 p-6 border-b border-stone-200 shadow-inner',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 561 },
                                },
                                React.createElement(
                                  'div',
                                  {
                                    className: 'max-w-5xl mx-auto',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 562 },
                                  },
                                  React.createElement(
                                    'h4',
                                    {
                                      className:
                                        'text-xs uppercase tracking-widest font-bold text-stone-500 mb-4 flex items-center gap-2',
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 563 },
                                    },
                                    React.createElement(ShoppingBag, {
                                      size: 14,
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 564 },
                                    }),
                                    ' Recent Purchase History'
                                  ),

                                  loadingOrders[member.id]
                                    ? React.createElement(
                                        'div',
                                        {
                                          className: 'flex justify-center py-8',
                                          __self: this,
                                          __source: { fileName: _jsxFileName, lineNumber: 568 },
                                        },
                                        React.createElement('div', {
                                          className:
                                            'w-6 h-6 border-2 border-gold-600 border-t-transparent rounded-full animate-spin',
                                          __self: this,
                                          __source: { fileName: _jsxFileName, lineNumber: 569 },
                                        }),
                                        React.createElement(
                                          'span',
                                          {
                                            className: 'ml-2 text-xs text-stone-500',
                                            __self: this,
                                            __source: { fileName: _jsxFileName, lineNumber: 570 },
                                          },
                                          'Loading orders...'
                                        )
                                      )
                                    : orders.length > 0
                                      ? React.createElement(
                                          'div',
                                          {
                                            className:
                                              'bg-white border border-stone-200 rounded-sm overflow-hidden',
                                            __self: this,
                                            __source: { fileName: _jsxFileName, lineNumber: 573 },
                                          },
                                          React.createElement(
                                            'table',
                                            {
                                              className: 'w-full text-left text-xs',
                                              __self: this,
                                              __source: { fileName: _jsxFileName, lineNumber: 574 },
                                            },
                                            React.createElement(
                                              'thead',
                                              {
                                                className: 'bg-stone-100 text-stone-500 uppercase',
                                                __self: this,
                                                __source: {
                                                  fileName: _jsxFileName,
                                                  lineNumber: 575,
                                                },
                                              },
                                              React.createElement(
                                                'tr',
                                                {
                                                  __self: this,
                                                  __source: {
                                                    fileName: _jsxFileName,
                                                    lineNumber: 576,
                                                  },
                                                },
                                                React.createElement(
                                                  'th',
                                                  {
                                                    className: 'px-4 py-3',
                                                    __self: this,
                                                    __source: {
                                                      fileName: _jsxFileName,
                                                      lineNumber: 577,
                                                    },
                                                  },
                                                  'Order ID'
                                                ),
                                                React.createElement(
                                                  'th',
                                                  {
                                                    className: 'px-4 py-3',
                                                    __self: this,
                                                    __source: {
                                                      fileName: _jsxFileName,
                                                      lineNumber: 578,
                                                    },
                                                  },
                                                  'Date'
                                                ),
                                                React.createElement(
                                                  'th',
                                                  {
                                                    className: 'px-4 py-3',
                                                    __self: this,
                                                    __source: {
                                                      fileName: _jsxFileName,
                                                      lineNumber: 579,
                                                    },
                                                  },
                                                  'Items'
                                                ),
                                                React.createElement(
                                                  'th',
                                                  {
                                                    className: 'px-4 py-3',
                                                    __self: this,
                                                    __source: {
                                                      fileName: _jsxFileName,
                                                      lineNumber: 580,
                                                    },
                                                  },
                                                  'Status'
                                                ),
                                                React.createElement(
                                                  'th',
                                                  {
                                                    className: 'px-4 py-3 text-right',
                                                    __self: this,
                                                    __source: {
                                                      fileName: _jsxFileName,
                                                      lineNumber: 581,
                                                    },
                                                  },
                                                  'Total'
                                                )
                                              )
                                            ),
                                            React.createElement(
                                              'tbody',
                                              {
                                                className: 'divide-y divide-stone-100',
                                                __self: this,
                                                __source: {
                                                  fileName: _jsxFileName,
                                                  lineNumber: 584,
                                                },
                                              },
                                              orders
                                                .slice(0, 5)
                                                .map((order) =>
                                                  React.createElement(
                                                    'tr',
                                                    {
                                                      key: order.id,
                                                      className: 'hover:bg-stone-50',
                                                      __self: this,
                                                      __source: {
                                                        fileName: _jsxFileName,
                                                        lineNumber: 586,
                                                      },
                                                    },
                                                    React.createElement(
                                                      'td',
                                                      {
                                                        className:
                                                          'px-4 py-3 font-mono font-bold text-stone-900',
                                                        __self: this,
                                                        __source: {
                                                          fileName: _jsxFileName,
                                                          lineNumber: 587,
                                                        },
                                                      },
                                                      order.order_number || order.id
                                                    ),
                                                    React.createElement(
                                                      'td',
                                                      {
                                                        className: 'px-4 py-3 text-stone-500',
                                                        __self: this,
                                                        __source: {
                                                          fileName: _jsxFileName,
                                                          lineNumber: 590,
                                                        },
                                                      },
                                                      order.orderDate ||
                                                        order.created_at ||
                                                        order.date
                                                        ? new Date(
                                                            order.orderDate ||
                                                              order.created_at ||
                                                              order.date
                                                          ).toLocaleDateString()
                                                        : '-'
                                                    ),
                                                    React.createElement(
                                                      'td',
                                                      {
                                                        className: 'px-4 py-3',
                                                        __self: this,
                                                        __source: {
                                                          fileName: _jsxFileName,
                                                          lineNumber: 593,
                                                        },
                                                      },
                                                      _optionalChain([
                                                        order,
                                                        'access',
                                                        (_22) => _22.items,
                                                        'optionalAccess',
                                                        (_23) => _23.length,
                                                      ]) || 1,
                                                      ' item(s)'
                                                    ),
                                                    React.createElement(
                                                      'td',
                                                      {
                                                        className: 'px-4 py-3',
                                                        __self: this,
                                                        __source: {
                                                          fileName: _jsxFileName,
                                                          lineNumber: 596,
                                                        },
                                                      },
                                                      React.createElement(
                                                        'span',
                                                        {
                                                          className: `inline-block px-2 py-0.5 rounded-full text-[9px] uppercase font-bold ${getOrderStatusBadge(order.status)}`,
                                                          __self: this,
                                                          __source: {
                                                            fileName: _jsxFileName,
                                                            lineNumber: 597,
                                                          },
                                                        },
                                                        order.status || 'Pending'
                                                      )
                                                    ),
                                                    React.createElement(
                                                      'td',
                                                      {
                                                        className:
                                                          'px-4 py-3 text-right font-mono text-stone-900',
                                                        __self: this,
                                                        __source: {
                                                          fileName: _jsxFileName,
                                                          lineNumber: 601,
                                                        },
                                                      },
                                                      formatCurrency(
                                                        order.total_amount ||
                                                          order.total ||
                                                          order.totalAmount ||
                                                          0
                                                      )
                                                    )
                                                  )
                                                )
                                            )
                                          ),
                                          orders.length > 5 &&
                                            React.createElement(
                                              'div',
                                              {
                                                className:
                                                  'px-4 py-2 text-center text-[10px] text-stone-400 border-t border-stone-100',
                                                __self: this,
                                                __source: {
                                                  fileName: _jsxFileName,
                                                  lineNumber: 609,
                                                },
                                              },
                                              '+ ',
                                              orders.length - 5,
                                              ' more orders'
                                            )
                                        )
                                      : React.createElement(
                                          'div',
                                          {
                                            className:
                                              'text-center py-8 bg-white border border-stone-200 rounded-sm',
                                            __self: this,
                                            __source: { fileName: _jsxFileName, lineNumber: 615 },
                                          },
                                          React.createElement(ShoppingBag, {
                                            size: 32,
                                            className: 'mx-auto text-stone-300 mb-2',
                                            __self: this,
                                            __source: { fileName: _jsxFileName, lineNumber: 616 },
                                          }),
                                          React.createElement(
                                            'p',
                                            {
                                              className: 'text-sm text-stone-400 italic',
                                              __self: this,
                                              __source: { fileName: _jsxFileName, lineNumber: 617 },
                                            },
                                            'No recent orders found.'
                                          ),
                                          React.createElement(
                                            'p',
                                            {
                                              className: 'text-xs text-stone-400 mt-1',
                                              __self: this,
                                              __source: { fileName: _jsxFileName, lineNumber: 618 },
                                            },
                                            'Orders will appear here after member makes a purchase.'
                                          )
                                        )
                                )
                              )
                            )
                        );
                      })
                    : React.createElement(
                        'tr',
                        { __self: this, __source: { fileName: _jsxFileName, lineNumber: 629 } },
                        React.createElement(
                          'td',
                          {
                            colSpan: 8,
                            className: 'px-6 py-12 text-center',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 630 },
                          },
                          React.createElement(User, {
                            size: 32,
                            className: 'mx-auto text-stone-300 mb-3',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 631 },
                          }),
                          React.createElement(
                            'p',
                            {
                              className: 'text-stone-500 text-sm',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 632 },
                            },
                            'No members found'
                          ),
                          React.createElement(
                            'p',
                            {
                              className: 'text-xs text-stone-400 mt-2',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 633 },
                            },
                            searchTerm
                              ? 'Try adjusting your search filters'
                              : 'Members will appear here after they register'
                          )
                        )
                      )
                )
              )
            ),

        /* Footer with count */
        React.createElement(
          'div',
          {
            className:
              'px-6 py-4 border-t border-stone-100 bg-stone-50/50 flex justify-between items-center',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 645 },
          },
          React.createElement(
            'span',
            {
              className: 'text-xs text-stone-500',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 646 },
            },
            'Showing ',
            filteredMembers.length,
            ' of ',
            localMembers.length,
            ' members'
          ),
          React.createElement(
            'div',
            {
              className: 'flex items-center gap-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 649 },
            },
            React.createElement(
              'span',
              {
                className: 'text-xs text-stone-400',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 650 },
              },
              'Last updated: ',
              new Date().toLocaleDateString()
            )
          )
        )
      )
    )
  );
};

export default AdminMembers;
